"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from "motion/react";
import {
  closeCartDrawer,
  getCartDrawerOpener,
  refresh,
  startCheckout,
  useCart,
  useCartDrawer,
} from "../../lib/cart-store";
import CartLines from "./CartLines";
import FreeShippingMeter from "./FreeShippingMeter";

/**
 * Slide-in cart, mounted once in the root layout.
 *
 * Adding to the cart anywhere — the inline quick-add panel, the PDP, the grid's
 * one-click add — opens this instead of leaving the shopper to notice a number
 * change in the header. The /cart page still exists and is still the checkout
 * surface of record; this is the fast path back to shopping.
 *
 * Accessibility mirrors MobileNav: focus moves in, Tab is trapped, Escape
 * closes, body scroll is locked, and focus returns to wherever it came from.
 */
export default function CartDrawer() {
  const open = useCartDrawer();
  const cart = useCart();
  const reduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    if (!open) return;

    const fallbackFocus = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        closeCartDrawer();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      (getCartDrawerOpener() ?? fallbackFocus)?.focus?.();
    };
  }, [open]);

  async function handleCheckout() {
    setError(null);
    setCheckingOut(true);
    try {
      const url = await startCheckout();
      window.location.href = url; // hands off to Wix's hosted checkout
    } catch (err) {
      console.error("[checkout] failed", err);
      setError("Checkout could not start. Please try again in a moment.");
      setCheckingOut(false);
    }
  }

  const hasItems = cart.lineItems.length > 0;

  return (
    <LazyMotion features={domAnimation} strict>
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[120]">
          <m.button
            type="button"
            aria-label="Close cart"
            tabIndex={-1}
            onClick={closeCartDrawer}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.2 }}
            className="absolute inset-0 bg-ink/45"
          />
          <m.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Your cart"
            initial={{ x: reduceMotion ? 0 : "100%", opacity: reduceMotion ? 0 : 1 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: reduceMotion ? 0 : "100%", opacity: reduceMotion ? 0 : 1 }}
            transition={{ duration: reduceMotion ? 0 : 0.28, ease: "easeOut" }}
            className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-paper text-ink shadow-[-24px_0_50px_-24px_rgba(10,10,10,0.35)]"
          >
            <div className="flex items-center justify-between border-b border-ink/10 px-6 py-4">
              <p className="text-[11px] uppercase tracking-[0.15em]">
                Your Cart{cart.count > 0 ? ` (${cart.count})` : ""}
              </p>
              <button
                ref={closeRef}
                type="button"
                aria-label="Close cart"
                onClick={closeCartDrawer}
                className="rounded-full p-1.5 text-ink/70 transition hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6">
              {cart.status === "error" && (
                <p className="mt-4 border border-ink/20 bg-panel px-4 py-3 text-[12px] text-ink/70">
                  {hasItems
                    ? "We couldn't refresh your cart — showing the last version we have."
                    : "We couldn't load your cart just now."}{" "}
                  <button type="button" onClick={() => refresh()} className="underline">
                    Try again
                  </button>
                </p>
              )}

              {cart.status === "loading" ? (
                <p className="py-16 text-center text-[13px] text-ink/60">Loading your cart…</p>
              ) : hasItems ? (
                <CartLines items={cart.lineItems} onError={setError} compact />
              ) : cart.status === "error" ? null : (
                <div className="py-16 text-center">
                  <p className="mb-6 text-[13px] text-ink/60">Your cart is empty.</p>
                  <Link
                    href="/shop"
                    onClick={closeCartDrawer}
                    className="inline-flex border border-ink/40 px-8 py-3 text-[11px] uppercase tracking-[0.18em] transition hover:bg-ink hover:text-paper"
                  >
                    Shop the Collection
                  </Link>
                </div>
              )}
            </div>

            {hasItems && (
              <div className="border-t border-ink/10 bg-panel px-6 py-5">
                <FreeShippingMeter subtotal={cart.subtotalNumber} />
                <div className="mt-4 flex items-center justify-between">
                  <span className="eyebrow text-ink/60">Subtotal</span>
                  <span className="text-lg">{cart.subtotal ?? "—"}</span>
                </div>
                <p className="mt-1 text-[12px] text-ink/60">
                  Shipping &amp; taxes calculated at checkout.
                </p>
                <button
                  type="button"
                  onClick={handleCheckout}
                  disabled={checkingOut}
                  className="mt-4 w-full bg-ink px-8 py-4 text-[12px] uppercase tracking-[0.18em] text-paper transition hover:bg-gold-deep disabled:opacity-40"
                >
                  {checkingOut ? "Starting checkout…" : "Checkout"}
                </button>
                <Link
                  href="/cart"
                  onClick={closeCartDrawer}
                  className="mt-3 block min-h-6 py-1 text-center text-[11px] uppercase tracking-[0.15em] text-ink/60 underline transition hover:text-ink"
                >
                  View full cart
                </Link>
                {error && (
                  <p role="alert" className="mt-3 text-center text-[12px] text-red-700">
                    {error}
                  </p>
                )}
              </div>
            )}
          </m.div>
        </div>
      )}
    </AnimatePresence>
    </LazyMotion>
  );
}
